import type { $TSFixMe, EventPropertiesMap } from '@readme/iso';

import * as amplitude from '@amplitude/analytics-browser';
import { AMPLITUDE_EVENT, AMPLITUDE_EVENT_PROPERTY } from '@readme/iso/src/amplitude';
import { useCallback, useContext, useMemo, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import type { ConfigContextValue, ProjectContextValue } from '@core/context';
import { ProjectContext, UserContext, ConfigContext } from '@core/context';
import useDebounced from '@core/hooks/useDebounced';
import useUserPermissions from '@core/hooks/useUserPermissions';
import { useSuperHubStore } from '@core/store';

// Global flag to ensure only one instance handles page view tracking
let globalPageViewTracking = false;
let globalLastPathname = '';

/**
 * Note:
 * useAmplitude uses useLocation from react-router-dom to track page views.
 * Hence, we need to use MemoryRouter from react-router-dom to test components that use this hook.
 */

export default function useAmplitude() {
  const { project } = useContext(ProjectContext) as ProjectContextValue;
  const user = useContext(UserContext) as $TSFixMe;
  const isSuperHub = useSuperHubStore(s => s.isSuperHub);
  const { pathname } = useLocation();

  // The shape of user context differs between hub and dash
  const email = user?.user?.email || user?.email;

  const {
    releaseVersion,
    amplitude: { apiKey: amplitudePublicKey, enabled } = {
      apiKey: '',
      enabled: false,
    },
  } = useContext(ConfigContext) as ConfigContextValue;

  const { isAdminUser } = useUserPermissions();

  // Use a ref to track if Amplitude has been initialized to prevent multiple initializations
  const isInitialized = useRef(false);

  // Initialize Amplitude once per session using useMemo
  useMemo(() => {
    if (typeof window !== 'undefined' && enabled && !isInitialized.current) {
      amplitude.init(amplitudePublicKey, email, {
        defaultTracking: false,
        deviceId: window.localStorage.getItem('amplitude_device_id') || undefined,
        autocapture: {
          attribution: false,
          // Disable pageViews autocapture since we'll handle it manually with debouncing
          // Autotracking page views is too volatile because we are changing the pathname in quick succession
          // it goes: /docs -> /docs#/ -> /docs/getting-started#/
          pageViews: false,
          sessions: false,
          formInteractions: true,
          fileDownloads: false,
          elementInteractions: {
            cssSelectorAllowlist: ['a', 'button'],
            actionClickAllowlist: ['div'],
          },
          networkTracking: false,
        },
      });
      const deviceId = amplitude.getDeviceId();
      if (deviceId) {
        window.localStorage.setItem('amplitude_device_id', deviceId);
      }
      isInitialized.current = true;
    }
  }, [enabled, amplitudePublicKey, email]);

  // Only allow one instance to handle page view tracking globally
  const pageViewCb = useCallback(() => {
    if (!enabled || !isInitialized.current) return;

    if (pathname === globalLastPathname || globalPageViewTracking) {
      return;
    }

    // Set global flag to prevent other instances from tracking
    globalPageViewTracking = true;

    amplitude.track(`Page View: ${pathname}`, {
      page_path: pathname,
      page_title: document.title,
      page_url: window.location.href,
    });

    globalLastPathname = pathname;

    // Reset global flag after a short delay
    setTimeout(() => {
      globalPageViewTracking = false;
    }, 100);
  }, [enabled, pathname]);

  const debouncedPageView = useDebounced(pageViewCb, 200);

  useEffect(() => {
    debouncedPageView();
  }, [debouncedPageView]);

  /**
   * NOTE: The setGroup calls are moved to the track function call, I don't really like this approach but its the
   * only way to consistently set group properties. Setting them 'globally' in the useEffect causes too much
   * requests, moving it to the track call reduces this, but it's not ideal.
   */

  // Core data included with every tracking event
  const eventMetaData = useMemo(() => {
    const { metrics } = project;

    return {
      ...(metrics && { metrics: { monthlyPurchaseLimit: metrics.monthlyPurchaseLimit } }),
      isSuperHub,
      permission: isAdminUser ? 'admin' : 'readonly',
      releaseVersion,
    };
  }, [isAdminUser, isSuperHub, project, releaseVersion]);

  // `amplitude.track` wrapper -> consume our core data, along with anything else passed in
  const track = useCallback(
    <T extends AMPLITUDE_EVENT>(
      event: T,
      eventProperties?: T extends keyof EventPropertiesMap ? EventPropertiesMap[T] : Record<string, unknown>,
    ) => {
      const subdomain = project.subdomain;
      const plan = project.plan;

      // Set groups for this track call
      amplitude.setGroup('project_id', project._id);
      amplitude.setGroup('subdomain', subdomain);
      amplitude.setGroup('plan', plan);

      return enabled && amplitude.track(event, { ...eventMetaData, ...eventProperties });
    },
    [enabled, eventMetaData, project._id, project.subdomain, project.plan],
  );

  // Set user ID for Amplitude client (i.e. in signup flows as it becomes known)
  const setUserID = useCallback((userID: string) => enabled && amplitude.setUserId(userID), [enabled]);

  // Only expose true Amplitude SDK methods if enabled
  if (!enabled) return { setUserID: () => {}, track: () => {} };

  // Expose core methods
  return { setUserID, track };
}

// Export event enums here as well for easy access when importing hook
export { AMPLITUDE_EVENT, AMPLITUDE_EVENT_PROPERTY };
